import React, { useState, useEffect } from 'react';
import { Dropdown, Badge, Avatar, Button, Typography } from 'antd';
import { BellRing, ExternalLink } from 'lucide-react';
import usecustomStyles from './customStyles';
import { StyleSimpleBar } from './SidebarStyle';
import { supabase } from '../services/supabase';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const customStyles = usecustomStyles();

const NotificationDropdown = () => {
    const [dbNotifications, setDbNotifications] = useState([]);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const formatTimeText = (dateInput) => {
        const createdDate = new Date(dateInput);
        const diffMs = Date.now() - createdDate.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 60) {
            return `${diffMin}m ago`;
        }
        const diffHrs = Math.floor(diffMin / 60);
        if (diffHrs < 24) {
            return `${diffHrs}h ago`;
        }
        const day = String(createdDate.getDate()).padStart(2, '0');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[createdDate.getMonth()];
        const year = createdDate.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const fetchNewInquiries = async () => {
        try {
            const { data, error } = await supabase.functions.invoke("admin-management", {
                body: { action: "get_brand_inquiries", status: "NEW" },
            });
            if (error) throw error;
            if (data?.success) {
                const fetched = data.inquiries || [];
                const mapped = fetched.map(item => {
                    return {
                        id: `inquiry-${item.id}`,
                        text: `New Brand Enquiry: "${item.brand_name}" - Case Ref #${item.case_ref.substring(0, 8).toUpperCase()} (${item.rep_name})`,
                        time: formatTimeText(item.created_at),
                        unread: true,
                        icon: <Avatar style={{ backgroundColor: '#fef3c7', color: '#d97706' }} icon={<BellRing size={14} />} />,
                        original: item
                    };
                });
                setDbNotifications(mapped);
            }
        } catch (err) {
            console.error("Failed to load new brand inquiries for notification badge:", err);
        }
    };

    useEffect(() => {
        fetchNewInquiries();

        const channel = supabase
            .channel('realtime-brand-inquiries-badge')
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'brand_inquiries' },
                () => {
                    fetchNewInquiries();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClearAll = async () => {
        try {
            const promises = dbNotifications.map(n => {
                if (n.original) {
                    return supabase.functions.invoke("admin-management", {
                        body: {
                            action: "update_brand_inquiry",
                            id: n.original.id,
                            status: "PENDING"
                        }
                    });
                }
                return Promise.resolve();
            });
            await Promise.all(promises);
            setDbNotifications([]);
            setOpen(false);
        } catch (err) {
            console.error("Failed to clear DB notifications:", err);
        }
    };

    const handleMarkAsRead = async (notifId, inquiryId) => {
        try {
            await supabase.functions.invoke("admin-management", {
                body: {
                    action: "update_brand_inquiry",
                    id: inquiryId,
                    status: "PENDING"
                }
            });
            setDbNotifications(prev => prev.filter(n => n.id !== notifId));
        } catch (err) {
            console.error("Failed to mark notification as read:", err);
        }
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read
        if (notification.original) {
            await handleMarkAsRead(notification.id, notification.original.id);
        }
        // Close the dropdown
        setOpen(false);
        // Navigate to brand enquiries and pre-fill the search with the case ref
        const caseRef = notification.original?.case_ref || '';
        const searchQuery = caseRef ? `?search=${encodeURIComponent(caseRef)}` : '';
        navigate(`/brand-enquiries${searchQuery}`);
    };

    const notificationCount = dbNotifications.length;

    const notificationMenu = [
        {
            label: <div>
                <div>
                    <div style={{ padding: customStyles.paddingSM, borderBottom: "1px solid", borderBottomColor: customStyles.bglight }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                            <h6 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "0px" }}>
                                Notifications{' '}
                                <Badge style={{ backgroundColor: customStyles.colorDangerBg, color: customStyles.colorDanger, fontSize: "13px", fontWeight: "bold" }} count={notificationCount} ></Badge>
                            </h6>
                            <Button type="primary" onClick={handleClearAll} size="small">
                                Clear All
                            </Button>
                        </div>
                        <p style={{ fontSize: "14px", marginBottom: "0px" }}>
                            You have{' '}
                            <span style={{ fontWeight: "bold" }}>
                                {notificationCount}
                            </span>{' '}
                            unread messages
                        </p>
                    </div>
                </div>
                <StyleSimpleBar style={{ height: "300px", width: "310px" }}>
                    <div style={{ padding: "15px 0px" }} id="notificationItemsTabContent" >
                        <h6 style={{ fontSize: "13px", margin: "0px 0px 8px 15px", textTransform: "uppercase" }} >
                            New
                        </h6>
                        {dbNotifications.length === 0 ? (
                            <div style={{ padding: "20px", textTransform: "uppercase", fontSize: "11px", textAlign: "center", color: "#a8a29e" }}>
                                No new notifications
                            </div>
                        ) : (
                            dbNotifications.map((notification) => (
                                <div
                                    style={{
                                        padding: "15px",
                                        cursor: "pointer",
                                        transition: "background 0.15s",
                                        borderBottom: "1px solid rgba(0,0,0,0.04)"
                                    }}
                                    className={`text-reset notification-item d-block dropdown-item position-relative unread-message`}
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    onMouseEnter={e => e.currentTarget.style.background = "rgba(251,191,36,0.06)"}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                    title="Click to open Brand Enquiries"
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                        <div style={{ marginRight: customStyles.marginXS }}>
                                            {notification.icon}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0, marginRight: '8px' }}>
                                            <h6 style={{ fontSize: "14px", marginBottom: customStyles.marginXS, lineHeight: "base" }}>{notification.text}</h6>
                                            <p style={{ fontSize: "11px", textTransform: "uppercase", marginBottom: "0px" }}>
                                                <span>
                                                    <ClockCircleOutlined /> {notification.time}
                                                </span>
                                            </p>
                                        </div>
                                        <Text style={{ fontSize: "13px", color: "#d97706", flexShrink: 0, marginTop: "2px" }}>
                                            <ExternalLink size={13} />
                                        </Text>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </StyleSimpleBar>
            </div>,
            key: '0',
        }
    ]

    return (
        <Dropdown
            menu={{ items: notificationMenu }}
            trigger={['click']}
            placement="bottomRight"
            open={open}
            onOpenChange={setOpen}
        >
            <div
                type="text"
                id="notificationDropdown"
                style={{ marginTop: "7px", marginRight: customStyles.marginXS, cursor: 'pointer' }}
            >
                <Badge count={notificationCount} size="small" style={{boxShadow: "none"}}>
                    <BellRing size={20} style={{ marginTop: "2px" }} />
                </Badge>
            </div>
        </Dropdown>
    );
}

export default NotificationDropdown;
